import { Injectable } from '@angular/core';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { ResponsiveService } from './responsive.service';

@Injectable()
export class CustomToastersService {
  constructor(private readonly toastrService: ToastrService, private readonly responsiveService: ResponsiveService) {
    this.toastrService.toastrConfig = { ...this.toastrService.toastrConfig, maxOpened: 10, preventDuplicates: true };
  }

  private loggedOut = false;
  public waitSave(): void {
    this.info('Please wait...', 'Saving');
  }

  public waitLoad(): void {
    this.info('Please wait...', 'Loading');
  }

  public waitRemove(): void {
    this.info('Please wait...', 'Removing');
  }

  public waitDelete(persistent = false): void {
    this.info('Please wait...', 'Deleting', persistent ? { disableTimeOut: true } : undefined);
  }

  public waitProcessing(): void {
    this.info('Please wait...', 'Processing');
  }

  public completeSave(text?: string): void {
    const message = text ?? 'Complete';

    if (!this.loggedOut) {
      this.clear();
      this.success(message, 'Save');
    }
  }

  public failSave(): void {
    if (!this.loggedOut) {
      this.clear();
      this.error('Failed', 'Save');
    }
  }

  public completeRemove(text?: string): void {
    const message = text ?? 'Complete';

    if (!this.loggedOut) {
      this.clear();
      this.success(message, 'Remove');
    }
  }

  public failRemove(): void {
    if (!this.loggedOut) {
      this.clear();
      this.error('Failed', 'Remove');
    }
  }

  public completeDelete(): void {
    if (!this.loggedOut) {
      this.clear();
      this.success('Complete', 'Delete');
    }
  }

  public completeProcessing(): void {
    if (!this.loggedOut) {
      this.clear();
      this.success('Complete', 'Processing');
    }
  }

  public failProcessing(): void {
    if (!this.loggedOut) {
      this.clear();
      this.error('Failed', 'Processing');
    }
  }

  public failLoad(): void {
    if (!this.loggedOut) {
      this.clear();
      this.error('Failed', 'Loading');
    }
  }

  public failPrint(): void {
    if (!this.loggedOut) {
      this.clear();
      this.error('Failed', 'Printing');
    }
  }

  public failDelete(): void {
    if (!this.loggedOut) {
      this.clear();
      this.error('Failed', 'Delete');
    }
  }

  public clear(): void {
    this.toastrService.clear();
  }

  public serverError(error: HttpErrorResponse): void {
    this.error(
      `Server Request Error ${error.status} - ${error.statusText} ${
        error.error && error.error.Message ? `: ${error.error.Message}` : ''
      }`,
      'Server Error'
    );
  }

  public customComplete(message: string, customTitle: string): void {
    if (!this.loggedOut) {
      this.clear();
      this.success(message, customTitle);
    }
  }

  public customInfo(message: string, customTitle: string): void {
    this.info(message, customTitle);
  }

  public customError(error: string, customTitle: string = null, properties?: Partial<IndividualConfig>): void {
    if (!this.loggedOut) {
      this.clear();
      this.error(error, customTitle, properties);
    }
  }

  public customErrorWithCallBack(error: string, customTitle: string = null, properties?: Partial<IndividualConfig>) {
    if (!this.loggedOut) {
      this.clear();
      return this.error(error, customTitle, properties);
    }
    return null;
  }

  public customWarning(warning: string, customTitle: string = null, properties?: Partial<IndividualConfig>): void {
    if (!this.loggedOut) {
      this.clear();
      this.warning(warning, customTitle, properties);
    }
  }

  public unauthorized(): void {
    this.loggedOut = true;
    this.clear();
    this.error('Please, login one more time.', 'Unauthorized');
    setTimeout(() => {
      this.loggedOut = false;
    }, 5000);
  }

  public alertInfo(message: string, customTitle: string): void {
    this.warning(message, customTitle);
  }

  private info(message?: string, title?: string, config?: Partial<IndividualConfig>) {
    return this.show(message, title, 'toast-info', config);
  }

  private success(message?: string, title?: string, config?: Partial<IndividualConfig>) {
    return this.show(message, title, 'toast-success', config);
  }

  private warning(message?: string, title?: string, config?: Partial<IndividualConfig>) {
    return this.show(message, title, 'toast-warning', {
      closeButton: true,
      disableTimeOut: true,
      ...config
    });
  }

  private error(message?: string, title?: string, config?: Partial<IndividualConfig>) {
    return this.show(message, title, 'toast-error', {
      closeButton: true,
      disableTimeOut: true,
      ...config
    });
  }

  private show(message?: string, title?: string, type?: string, config?: Partial<IndividualConfig>) {
    if (this.responsiveService.isMobileWidth()) {
      this.clear();
    }
    return this.toastrService.show(message, title, config, type);
  }
}
